import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, PlusCircle, Receipt, Bell, Scale, ShieldCheck, User, Sun, Moon, LogOut, Wifi, WifiOff } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, id: "nav-tab-dashboard" },
  { to: "/add", label: "Add", icon: PlusCircle, id: "nav-tab-add" },
  { to: "/receipts", label: "Receipts", icon: Receipt, id: "nav-tab-receipts" },
  { to: "/notifications", label: "Alerts", icon: Bell, id: "nav-tab-notifications" },
  { to: "/balance", label: "Balance", icon: Scale, id: "nav-tab-balance" },
];

function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);
  return [theme, setTheme];
}

export function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, household, role, logout } = useAuth();
  const { connected } = useRealtime();
  const [theme, setTheme] = useTheme();

  const { data: notifData } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 30000,
  });
  const unread = notifData?.unread || 0;

  const navItems = role === "admin" ? [...NAV, { to: "/admin", label: "Admin", icon: ShieldCheck, id: "nav-tab-admin" }] : NAV;
  const isActive = (to) => location.pathname === to;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2.5" data-testid="app-logo">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-heading font-bold text-lg">R</div>
            <div className="text-left leading-tight hidden sm:block">
              <div className="font-heading font-bold text-base">RoomieSplit</div>
              <div className="text-[11px] text-muted-foreground truncate max-w-[160px]">{household?.name}</div>
            </div>
          </button>

          <div className="flex items-center gap-1.5">
            <span
              className={`flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full ${connected ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400" : "text-muted-foreground bg-secondary"}`}
              data-testid="live-status"
              title={connected ? "Live updates on" : "Reconnecting"}
            >
              {connected ? <Wifi className="h-3 w-3 live-dot" /> : <WifiOff className="h-3 w-3" />}
              <span className="hidden sm:inline">{connected ? "Live" : "Offline"}</span>
            </span>

            <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 relative" onClick={() => navigate("/notifications")} data-testid="header-bell-btn">
              <Bell className="h-[18px] w-[18px]" />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center" data-testid="unread-badge">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Button>

            <Button variant="ghost" size="icon" className="rounded-full h-9 w-9" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle-btn">
              {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-0.5" data-testid="profile-menu-btn">
                  <Avatar className="h-9 w-9 border border-border">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">{user?.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="h-4 w-4 mr-2" /> Profile & Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-rose-600">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:block border-t border-border">
          <div className="max-w-6xl mx-auto px-4 flex gap-1">
            {navItems.map((n) => (
              <button
                key={n.to}
                onClick={() => navigate(n.to)}
                data-testid={n.id}
                className={`flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium border-b-2 transition-colors ${isActive(n.to) ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              >
                <n.icon className="h-4 w-4" /> {n.label}
              </button>
            ))}
          </div>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 pb-28 md:pb-10">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-border md:hidden px-1 py-1.5" data-testid="mobile-bottom-nav">
        <div className={`grid gap-0.5 ${navItems.length === 6 ? "grid-cols-6" : "grid-cols-5"}`}>
          {navItems.map((n) => {
            const active = isActive(n.to);
            return (
              <button
                key={n.to}
                onClick={() => navigate(n.to)}
                data-testid={`${n.id}-mobile`}
                className={`flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-lg transition-colors ${active ? "text-primary" : "text-muted-foreground"}`}
              >
                <div className="relative">
                  <n.icon className={`h-[21px] w-[21px] ${active ? "scale-110" : ""} transition-transform`} />
                  {n.to === "/notifications" && unread > 0 && (
                    <span className="absolute -top-1.5 -right-2 bg-rose-500 text-white text-[9px] font-bold min-w-[14px] h-3.5 px-0.5 rounded-full flex items-center justify-center">{unread > 9 ? "9" : unread}</span>
                  )}
                </div>
                <span className="text-[10px] font-medium">{n.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
